import React from "react"
import * as layouts from "layouts"
import * as api from "brandguard/api"
import * as bglayouts from "brandguard/components/layouts"
import classnames from "classnames"
import * as scores from "brandguard/components/layouts/scores"
import { useSettings } from "accounts/settings"
import { State } from "accounts/api"
import {
	LookAndFeel,
	Violence,
	Nudity,
	Explanation,
	BackgroundColor,
	FontDetection,
	ArtifactDetection,
	ProductDetection,
} from "brandguard/display/scores"
import LogoDetection from "brandguard/display/scores/logo_detection"

interface props extends layouts.containers.ContainerProps {
	item: api.VideoSearchResponseItem
	prediction(evt: api.VideoSearchResponseItem): api.Prediction
}

export default function Scoring(props: props): JSX.Element {
	const { item, prediction, className, mx, ...rest } = props
	const parentRest = { mx }
	const settings = useSettings()

	const videoAsMedia = item.media ? ({ id: item.media.id } as api.Media) : undefined

	const onBrand = {
		title: "Brand Principles",
		indev: false,
		subtitle: "Detailed view of the Brand Principles",
		subscores: [
			{
				title: "Look & Feel",
				score: item.event?.video_lookandfeel_conformance,
				inverse: false,
				indev: false,
				available: settings.video_lookandfeel,
				additions: videoAsMedia ? <LookAndFeel event={videoAsMedia} /> : undefined,
			},
			{
				title: "Brand Consistency",
				score: item.event?.artifact_score,
				inverse: false,
				indev: false,
				available: settings.brand_consistency,
				additions: <ArtifactDetection event={item.event!} />,
			},
		] as scores.subScoreStruct[],
	} as scores.scoreStruct

	const onStyle = {
		title: "Precision Elements",
		indev: false,
		subtitle: "Detailed view of the Precision Elements",
		subscores: [
			{
				title: "Correct Logo(s)",
				score: item.event?.video_logo_detection,
				inverse: false,
				indev: false,
				available: settings.video_logo_detection,
				additions: videoAsMedia ? <LogoDetection event={videoAsMedia} /> : undefined,
			},
			{
				title: "Correct Font(s)",
				score: item.event?.video_font_detection,
				inverse: false,
				indev: false,
				available: settings.video_font_detection,
				additions: videoAsMedia ? <FontDetection event={videoAsMedia} /> : undefined,
			},
			{
				title: "Colors",
				score: item.event?.video_background_colors,
				inverse: false,
				indev: false,
				available: settings.video_background_colors,
				additions: videoAsMedia ? <BackgroundColor event={videoAsMedia} /> : undefined,
			},
		] as scores.subScoreStruct[],
	} as scores.scoreStruct

	const safety = {
		title: "Brand Safety",
		indev: false,
		subtitle: "Detailed view of the Brand Safety score",
		subscores: [
			{
				title: "Violence Check",
				score: item.event?.video_violence,
				inverse: true,
				indev: false,
				available: settings.video_violence,
				additions: videoAsMedia ? <Violence event={videoAsMedia} /> : undefined,
			},
			{
				title: "Nudity Check",
				score: item.event?.video_sexually_explicit,
				inverse: true,
				indev: false,
				available: settings.video_sexually_explicit,
				additions: videoAsMedia ? <Nudity event={videoAsMedia} /> : undefined,
			},
			{
				title: "AI Generated Check",
				...scores.assessed(item.event?.video_ai_generated, item.event?.video_ai_generated_explanation ?? ""),
				inverse: true,
				indev: false,
				additions: <Explanation explanation={item.event?.video_ai_generated_explanation ?? ""} />,
			},
		] as scores.subScoreStruct[],
	} as scores.scoreStruct

	const quality = {
		title: "Quality",
		indev: false,
		subtitle: "Detailed view of the Quality score",
		subscores: [
			{
				title: "Video Quality",
				score: item.event?.video_quality_general,
				inverse: false,
				indev: false,
				available: settings.video_quality_general,
				additions: <Explanation explanation={item.event?.video_quality_general_explanation ?? ""} />,
			},
		] as scores.subScoreStruct[],
	} as scores.scoreStruct

	function NonNegotiableAlert(props: layouts.containers.FlexProps): JSX.Element {
		const { ...rest } = props

		const _onbrand = onBrand.subscores.map((i) => scores.isNonNegotiable(i.score, i.indev, i.inverse, i.available))
		const _onstyle = onStyle.subscores.map((i) => scores.isNonNegotiable(i.score, i.indev, i.inverse, i.available))
		const _safety = safety.subscores.map((i) => scores.isNonNegotiable(i.score, i.indev, i.inverse, i.available))
		const _quality = quality.subscores.map((i) => scores.isNonNegotiable(i.score, i.indev, i.inverse, i.available))
		if (!_onbrand.concat(_onstyle, _safety, _quality).includes(true)) return <></>
		return <scores.NonNegotiableMessage {...rest} />
	}

	const initOpen = (): boolean => {
		const _onbrand = scores.sum(onBrand.subscores.map((i) => i.available)) === 0 ? 0 : 1
		const _onstyle = scores.sum(onStyle.subscores.map((i) => i.available)) === 0 ? 0 : 1
		const _safety = scores.sum(safety.subscores.map((i) => i.available)) === 0 ? 0 : 1
		const _quality = scores.sum(quality.subscores.map((i) => i.available)) === 0 ? 0 : 1
		return _onbrand + _onstyle + _safety + _quality === 1
	}

	return (
		<bglayouts.fileDetails.InformationGroup
			className={classnames(className, "file-details-information-group")}
			{...rest}
		>
			{settings.confidence !== State.DISABLED && (
				<scores.BGScore
					confidence={item.event!.confidence}
					className={classnames(className, "brandguard-score")}
				/>
			)}
			<NonNegotiableAlert height="30px" borderRadius="5px" alignItems="center" mb="15px" {...parentRest} />
			<scores.ScoreItem i={onBrand} className={className} initOpen={initOpen()} />
			<scores.ScoreItem i={onStyle} className={className} initOpen={initOpen()} />
			<scores.ScoreItem i={safety} className={className} initOpen={initOpen()} />
			<scores.ScoreItem i={quality} className={className} initOpen={initOpen()} />
			{settings.video_product_detection !== State.DISABLED && videoAsMedia && (
				<ProductDetection event={videoAsMedia} contentType="video">
					<layouts.Span fontWeight="500">Detected Products</layouts.Span>
				</ProductDetection>
			)}
		</bglayouts.fileDetails.InformationGroup>
	)
}

Scoring.defaultProps = {
	prediction: (i: api.VideoSearchResponseItem) => i.event?.approved || api.Prediction.NONE,
}
